import SslSection from './SslSection'
import {
    ValidIcon,
    InvalidIcon,
    ExpireIcon,
    CalendarIcon
} from '../../../icons'
import {useEffect, useState} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {
    getSslAndTldData,
    setIsSslTldDataFetching
} from '../../../stores/monitoring'
import {DateFormats, formatWithGMT} from '../../../utils/date'
import reminders from '../../../data/reminders.json'
import {colors} from '../../../utils/colors'
import localStorageManager from '../../../utils/localStorageManager'
import localStorageKeys from '../../../utils/localStorageKeys'
import {wait} from '@testing-library/user-event/dist/utils'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const RETRY_LIMIT = 5
const RETRY_DELAY = 6000
const WAIT_TIME = 2000

const SslAndDomain = ({id, isDarkMode}) => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('monitoring')
    const sslData = useSelector((state) => state.monitoring.sslData)
    const tldData = useSelector((state) => state.monitoring.tldData)
    const isSslTldDataFetching = useSelector(
        (state) => state.monitoring.isSslTldDataFetching
    )
    const sslReminderData = useSelector(
        (state) => state.monitoring.sslReminderData
    )
    const tldReminderData = useSelector(
        (state) => state.monitoring.tldReminderData
    )
    const [isLoading, setIsLoading] = useState(false)

    const generateDataFromArray = (data) => {
        const result = []
        for (let i = 0; i < data.length; i += Math.ceil(data?.length / 2)) {
            const chunk = data.slice(i, i + Math.ceil(data?.length / 2))
            result.push(chunk)
        }
        return {
            firstHalf: result[0],
            secondHalf: result[1]
        }
    }

    const generateSslBody = () => {
        return sslData && !!Object.keys(sslData).length ? (
            <div className="body">
                {sslData?.domain && (
                    <div className="body-item">
                        <h5 className="name">{text.domain}</h5>
                        <span className="desc">
                            <a>{sslData?.domain}</a>
                        </span>
                    </div>
                )}
                {sslData?.issuer && (
                    <div className="body-item">
                        <h5 className="name">{text.issuer}</h5>
                        <p className="desc">{sslData?.issuer}</p>
                    </div>
                )}
                {sslData?.expires_at && !sslData?.expired && (
                    <div className="body-item">
                        <h5 className="name">{text.expires_at}</h5>
                        <span className="desc">
                            <ExpireIcon
                                stroke={
                                    sslData?.expires_after_days > 30
                                        ? colors.colorGreen
                                        : sslData?.expires_after_days < 30 &&
                      sslData?.expires_after_days > 5
                                            ? colors.colorOrange
                                            : colors.colorDarkRed
                                }
                            />
                            {formatWithGMT(sslData?.expires_at, DateFormats.FULL_WEEKDAY_AT)}
                        </span>
                    </div>
                )}
                {sslData?.expires_after_days > 0 && !sslData?.expired && (
                    <div className="body-item">
                        <h5 className="name">{text.expires_after}</h5>
                        <span className="desc">
                            <ExpireIcon
                                stroke={
                                    sslData?.expires_after_days > 30
                                        ? colors.colorGreen
                                        : sslData?.expires_after_days < 30 &&
                      sslData?.expires_after_days > 5
                                            ? colors.colorOrange
                                            : colors.colorDarkRed
                                }
                            />
                            {`${sslData?.expires_after_days} ${
                                sslData?.expires_after_days > 1 ? text.days_label : text.day_label
                            }`}
                        </span>
                    </div>
                )}
                {sslData?.issued_at && (
                    <div className="body-item">
                        <h5 className="name">{text.issued_at}</h5>
                        <p className="desc">{formatWithGMT(sslData?.issued_at, DateFormats.FULL_WEEKDAY_AT)}</p>
                    </div>
                )}
                {sslData?.dns_names && (
                    <div className="body-item">
                        <h5 className="name">{text.dns_names}</h5>
                        <span className="desc column">
                            <span className="nameserver">
                                {generateDataFromArray(sslData?.dns_names).firstHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                            <span className="nameserver">
                                {generateDataFromArray(sslData?.dns_names).secondHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                        </span>
                    </div>
                )}
                <div className="body-item">
                    <h5 className="name">{text.certificate_is}</h5>
                    <p className="desc">
                        {sslData?.is_valid ? <ValidIcon /> : <InvalidIcon />}
                        {sslData?.is_valid ? text.valid : text.invalid}
                    </p>
                </div>
                {sslData?.created_at && (
                    <div className="body-item">
                        <h5 className="name">{text.created_at}</h5>
                        <p className="desc">
                            <CalendarIcon width="22" height="22" stroke={isDarkMode ?  colors.colorFilterTitle : colors.colorMuted} />
                            {formatWithGMT(sslData?.created_at, DateFormats.FULL_WEEKDAY_AT)}
                        </p>
                    </div>
                )}
                {sslData?.checked_at && (
                    <div className="body-item">
                        <h5 className="name">{text.checked_at}</h5>
                        <p className="desc">
                            <CalendarIcon width="22" height="22" stroke={isDarkMode ?  colors.colorFilterTitle : colors.colorMuted} />
                            {formatWithGMT(sslData?.checked_at, DateFormats.FULL_WEEKDAY_AT)}
                        </p>
                    </div>
                )}
            </div>
        ) : (
            <div className="body">
                <div className="body-item">
                    <h5 className="name">{text.ssl_data_not_found}</h5>
                </div>
            </div>
        )
    }

    const generateDomainBody = () => {
        return tldData && !!Object.keys(tldData).length ? (
            <div className="body">
                {tldData?.domain && (
                    <div className="body-item">
                        <h5 className="name">{text.domain}</h5>
                        <span className="desc">
                            <a>{tldData?.domain}</a>
                        </span>
                    </div>
                )}
                {tldData?.expires_at && !tldData?.expired && (
                    <div className="body-item">
                        <h5 className="name">{text.expires_at}</h5>
                        <p className="desc">
                            <ExpireIcon
                                stroke={
                                    tldData?.expires_after_days > 30
                                        ? colors.colorGreen
                                        : tldData?.expires_after_days < 30 &&
                      tldData?.expires_after_days > 5
                                            ? colors.colorOrange
                                            : colors.colorDarkRed
                                }
                            />
                            {formatWithGMT(tldData?.expires_at, DateFormats.FULL_WEEKDAY_AT)}
                        </p>
                    </div>
                )}
                {tldData?.expires_after_days > 0 && !tldData?.expired && (
                    <div className="body-item">
                        <h5 className="name">{text.expires_after}</h5>
                        <p className="desc">
                            <ExpireIcon
                                stroke={
                                    tldData?.expires_after_days > 30
                                        ? colors.colorGreen
                                        : tldData?.expires_after_days < 30 &&
                      tldData?.expires_after_days > 5
                                            ? colors.colorOrange
                                            : colors.colorDarkRed
                                }
                            />
                            {`${tldData?.expires_after_days} ${
                                tldData?.expires_after_days > 1 ? text.days_label : text.day_label
                            }`}
                        </p>
                    </div>
                )}
                {tldData?.registrar && (
                    <div className="body-item">
                        <h5 className="name">{text.registrar}</h5>
                        <p className="desc">{tldData?.registrar}</p>
                    </div>
                )}
                {tldData?.name_servers && (
                    <div className="body-item">
                        <h5 className="name">{text.name_servers}</h5>
                        <span className="desc column">
                            <span className="nameserver">
                                {generateDataFromArray(tldData?.name_servers).firstHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                            <span className="nameserver">
                                {generateDataFromArray(tldData?.name_servers).secondHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                        </span>
                    </div>
                )}
                {tldData?.ips && (
                    <div className="body-item">
                        <h5 className="name">{text.ips}</h5>
                        <span className="desc column">
                            <span className="nameserver">
                                {generateDataFromArray(tldData?.ips).firstHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                            <span className="nameserver">
                                {generateDataFromArray(tldData?.ips).secondHalf?.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>
                                    }
                                )}
                            </span>
                        </span>
                    </div>
                )}
                {tldData?.created_at && (
                    <div className="body-item">
                        <h5 className="name">{text.created_at}</h5>
                        <p className="desc">
                            <CalendarIcon width="22" height="22" stroke={isDarkMode ?  colors.colorFilterTitle : colors.colorMuted} />
                            {formatWithGMT(tldData?.created_at, DateFormats.FULL_WEEKDAY_AT)}
                        </p>
                    </div>
                )}
                {tldData?.checked_at && (
                    <div className="body-item">
                        <h5 className="name">{text.checked_at}</h5>
                        <p className="desc">
                            <CalendarIcon width="22" height="22" stroke={isDarkMode ?  colors.colorFilterTitle : colors.colorMuted} />
                            {formatWithGMT(tldData?.checked_at, DateFormats.FULL_WEEKDAY_AT)}
                        </p>
                    </div>
                )}
            </div>
        ) : (
            <div className="body">
                <div className="body-item">
                    <h5 className="name">{text.domain_data_not_found}</h5>
                </div>
            </div>
        )
    }

    const clearFetchingInterval = (fetchingInterval) => {
        dispatch(setIsSslTldDataFetching(false))
        setIsLoading(false)
        clearInterval(fetchingInterval)
        return true
    }

    const fetchData = async() => {
        let retryCount = 0
        dispatch(setIsSslTldDataFetching(true))

        const fetchDataInternal = async() => {
            retryCount++

            if (!id) {
                return
            }

            const newCreatedResource = localStorageManager.get(localStorageKeys.CREATED_RESOURCES)?.[id]
            const success = await dispatch(getSslAndTldData(id))

            if (newCreatedResource && !localStorageManager.get(localStorageKeys.SSL_TLD)?.[id]) {
                dispatch(setIsSslTldDataFetching(false))
                setIsLoading(true)
            }

            if (success) {
                localStorageManager.set(localStorageKeys.SSL_TLD, {[id]: true})
            }

            if (!newCreatedResource) {
                localStorage.removeItem(localStorageKeys.SSL_TLD)
                clearFetchingInterval(interval)
            }

            if (success || retryCount === RETRY_LIMIT) {
                await wait(WAIT_TIME)
                clearFetchingInterval(interval)
            }
        }

        fetchDataInternal()
        const interval = setInterval(fetchDataInternal, RETRY_DELAY)

        return () => clearInterval(interval)
    }

    useEffect(() => {
        const fetchDataAsync = async() => {
            await fetchData()
        }

        fetchDataAsync()
    }, [id])

    return (
        <div className="ssl-domain">
            <>
                <SslSection
                    type={reminders.ssl.type}
                    setButtonEnabled={sslData?.expires_at}
                    data={sslReminderData}
                    title={text.ssl}
                    body={generateSslBody()}
                    isDataFetching={isSslTldDataFetching}
                    isDataLoading={isLoading}
                    isDarkMode={isDarkMode}
                />
                <SslSection
                    type={reminders.tld.type}
                    setButtonEnabled={tldData?.expires_at}
                    data={tldReminderData}
                    title={text.domain_title}
                    body={generateDomainBody()}
                    isDataFetching={isSslTldDataFetching}
                    isDataLoading={isLoading}
                    isDarkMode={isDarkMode}
                />
            </>
        </div>
    )
}

export default SslAndDomain
